// Iter117.7 — Shared inline error/warning/info notice.
//
// Replaces the dozen+ ad-hoc red boxes scattered across the cockpit
// (`text-[#FF3366] bg-[#FF3366]/10 border ...` literals) so error
// language stays as consistent as the new freshness language.
//
// Variants:
//   error   — red, AlertTriangle icon
//   warning — amber, AlertTriangle icon
//   info    — blue, AlertTriangle icon
//
// Optional behaviour:
//   onRetry    — renders a small "Retry" button on the right
//   onDismiss  — renders an X button on the right
//   testId     — explicit data-testid for the container

import React from "react";
import { AlertTriangle, X, RefreshCw } from "lucide-react";

const VARIANTS = {
  error:   { text: "text-[#FF3366]", border: "border-[#FF3366]/30", bg: "bg-[#FF3366]/10", icon: "text-[#FF3366]" },
  warning: { text: "text-[#FFB800]", border: "border-[#FFB800]/30", bg: "bg-[#FFB800]/10", icon: "text-[#FFB800]" },
  info:    { text: "text-[#22D3FF]", border: "border-[#22D3FF]/30", bg: "bg-[#22D3FF]/10", icon: "text-[#22D3FF]" },
};

export default function InlineError({
  variant = "error",
  children,
  message,
  onRetry,
  onDismiss,
  retryLabel = "Retry",
  testId,
  className = "",
}) {
  const v = VARIANTS[variant] || VARIANTS.error;
  const body = children ?? message;
  if (body == null || body === "") return null;
  return (
    <div
      data-testid={testId}
      className={`flex items-start gap-2 text-[10px] font-mono ${v.text} ${v.bg} border ${v.border} rounded-sm px-2 py-1.5 ${className}`}
      role={variant === "error" ? "alert" : "status"}
    >
      <AlertTriangle size={11} className={`flex-shrink-0 mt-0.5 ${v.icon}`} />
      <div className="flex-1 leading-relaxed break-words min-w-0">{body}</div>
      {onRetry && (
        <button
          type="button"
          data-testid={testId ? `${testId}-retry` : undefined}
          onClick={(e) => { e.stopPropagation(); onRetry(); }}
          className={`flex-shrink-0 inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-sm border ${v.border} ${v.text} hover:bg-[#0A0A0E]`}
        >
          <RefreshCw size={9} /> {retryLabel}
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          data-testid={testId ? `${testId}-dismiss` : undefined}
          onClick={(e) => { e.stopPropagation(); onDismiss(); }}
          aria-label="Dismiss"
          className={`flex-shrink-0 inline-flex items-center text-[#8A8A93] hover:text-white p-0.5 rounded-sm`}
        >
          <X size={11} />
        </button>
      )}
    </div>
  );
}
